export type PackDataInput = {
  dataMb?: number | null;
  dataGb?: number | null;
  name?: string | null;
};

export type PackDataFigure = { value: string; unit: 'MB' | 'GB' };

export type DetailTile = { label: string; value: string; span: boolean; chip?: string };

export type PackOperator = { name: string; type?: string | null };

const MB_PER_GB = 1000;

function trimNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function fromMegabytes(mb: number): PackDataFigure {
  if (mb >= MB_PER_GB) return { value: trimNumber(mb / MB_PER_GB), unit: 'GB' };
  return { value: String(Math.round(mb)), unit: 'MB' };
}

/** Prefer an explicit MB figure, then the pack name, then GB × 1000. */
export function formatPackData(input: PackDataInput): PackDataFigure {
  const dataMb = input.dataMb;
  if (typeof dataMb === 'number' && Number.isFinite(dataMb) && dataMb > 0) return fromMegabytes(dataMb);

  const name = input.name ?? '';
  const namedMb = name.match(/(\d+(?:\.\d+)?)\s*MB\b/i);
  if (namedMb) return fromMegabytes(Number(namedMb[1]));
  const namedGb = name.match(/(\d+(?:\.\d+)?)\s*GB\b/i);
  if (namedGb) {
    const gb = Number(namedGb[1]);
    if (gb >= 1) return { value: trimNumber(gb), unit: 'GB' };
    if (Math.abs(gb - 0.49) < 0.02 || Math.abs(gb - 0.5) < 0.02) return { value: '500', unit: 'MB' };
    if (Math.abs(gb - 0.1) < 0.02) return { value: '100', unit: 'MB' };
    return fromMegabytes(gb * MB_PER_GB);
  }

  const dataGb = input.dataGb;
  if (typeof dataGb === 'number' && Number.isFinite(dataGb) && dataGb > 0) {
    if (Math.abs(dataGb - 0.49) < 0.02 || Math.abs(dataGb - 0.5) < 0.02) return { value: '500', unit: 'MB' };
    if (Math.abs(dataGb - 0.1) < 0.02) return { value: '100', unit: 'MB' };
    if (dataGb >= 1) return { value: trimNumber(dataGb), unit: 'GB' };
    return fromMegabytes(dataGb * MB_PER_GB);
  }
  return { value: '0', unit: 'MB' };
}

export function formatInr(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export type TilePack = {
  dataMb?: number | null;
  dataGb?: number | null;
  name?: string | null;
  validityDays?: number | null;
  speed?: string | null;
  operators?: PackOperator[] | null;
  coverage?: string | null;
  topUp?: boolean | null;
  hotspot?: boolean | null;
  activation?: string | null;
  activateBy?: string | null;
  ipLocation?: string | null;
  maxPrivacy?: boolean | null;
  sms?: string | null;
  dataLabel?: string | null;
  durationLabel?: string | null;
  showData?: boolean;
  showDuration?: boolean;
  /** Set when the API already applied admin show/hide choices; take its fields as they are. */
  detailsResolved?: boolean;
};

export function displaySpeed(pack: TilePack): string {
  if (pack.detailsResolved) return pack.speed?.trim() || '';
  const types = (pack.operators ?? [])
    .map((operator) => operator.type?.trim() || '')
    .filter((type) => type !== '');
  if (types.length > 0 && types.every((type) => type === types[0])) return types[0];
  return pack.speed?.trim() || '';
}

/** Plan-row chip. The combined radio string is shown as 5G. */
export function tagSpeed(pack: TilePack): string {
  const shown = displaySpeed(pack);
  if (/3\s*g\s*\/\s*4\s*g\s*\/\s*5\s*g/i.test(shown)) return '5G';
  return shown;
}

const TILES: { label: string; read: (pack: TilePack) => string }[] = [
  {
    label: 'Data',
    read: (pack) => {
      if (pack.showData === false) return '';
      if (pack.dataLabel?.trim()) return pack.dataLabel.trim();
      const figure = formatPackData(pack);
      return figure.value === '0' ? '' : `${figure.value} ${figure.unit}`;
    },
  },
  {
    label: 'Duration',
    read: (pack) => {
      if (pack.showDuration === false) return '';
      if (pack.durationLabel?.trim()) return pack.durationLabel.trim();
      return pack.validityDays ? `${pack.validityDays} days` : '';
    },
  },
  { label: 'Speed', read: (pack) => displaySpeed(pack) },
  { label: 'Location', read: (pack) => pack.coverage?.trim() || '' },
  {
    label: 'Top-up',
    read: (pack) => (pack.topUp === true ? 'Yes' : pack.topUp === false ? 'No' : ''),
  },
  {
    label: 'Hotspot',
    read: (pack) => (pack.hotspot === true ? 'Yes' : pack.hotspot === false ? 'No' : ''),
  },
  { label: 'Activation', read: (pack) => pack.activation?.trim() || '' },
  { label: 'QR validity', read: (pack) => pack.activateBy?.trim() || '' },
  { label: 'IP location', read: (pack) => pack.ipLocation?.trim() || '' },
  {
    label: 'SMS',
    read: (pack) => {
      const value = pack.sms?.trim() || '';
      if (!value || /api/i.test(value)) return '';
      return value;
    },
  },
];

export function detailTiles(pack: TilePack): DetailTile[] {
  const tiles = TILES.map((tile) => ({
    label: tile.label,
    value: tile.read(pack).trim(),
    span: false,
    chip: tile.label === 'IP location' && pack.maxPrivacy === true ? 'Max Privacy' : undefined,
  })).filter((tile) => tile.value !== '');
  if (tiles.length % 2 === 1) tiles[tiles.length - 1].span = true;
  return tiles;
}
